import React, { useState, useSyncExternalStore, ReactElement } from 'react';
import { subscribeToNothing } from 'hooks/useHydrated';
import Image from 'next/image';
import { TransparentButton } from 'components/Button';
import Container from 'components/Container';
import HomeCover from 'components/HomeCover';
import { H2, P } from 'components/Text';
import SearchField from 'containers/SearchField';
import { travelSimpleIllustration as travelSimple } from 'assets/illustrations';
import { useTranslation } from 'next-i18next/pages';
import { serverSideTranslations } from 'next-i18next/pages/serverSideTranslations';
import PasswordResetModal from 'components/Modal/PasswordResetModal';
import Layout from 'components/Layout';
import { NextPageWithLayout } from './_app';
import { GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';
import RenderWhenNear from 'components/RenderWhenNear';
import { SECTION_Y, CARD_GAP } from 'components/TextPage/pageLayout';
import HolidayIdeas from 'components/HolidayIdeas';
import { PACKAGE_TOURS_IN_HOUSE } from 'utils/packageToursSwitch';

// Below the fold and filled from the API in the browser (they render nothing
// until their data arrives), so they load and render only when scrolled near:
// their code, the carousel and its measuring stay out of the page start-up.
const TravelTips = dynamic(() => import('components/TravelTips'), {
  ssr: false,
});
// WHIT-485 round 3: searched in the browser, with the visitor's today.
const TopOffers = dynamic(() => import('components/TopOffers'), {
  ssr: false,
});
const PopularDestinations = dynamic(
  () => import('components/FavouriteDestinations'),
  { ssr: false },
);

interface IHomeProps {}

const Home: NextPageWithLayout<IHomeProps> = () => {
  const { t } = useTranslation();
  //Have oobCode. False on the server and while hydrating.
  const hasOobCode = useSyncExternalStore(
    subscribeToNothing,
    () => window.location.search.indexOf('oobCode=') !== -1,
    () => false,
  );
  const [resetModalClosed, setResetModalClosed] = useState(false);
  // The search tab chosen below the hero; the hero follows it (WHIT-506).
  const [tab, setTab] = useState<string>('flights');
  const resetModalOpen = hasOobCode && !resetModalClosed;

  return (
    <div>
      {/* Home cover (background) */}
      <HomeCover tab={tab} />

      <PasswordResetModal
        closeModal={() => setResetModalClosed(true)}
        modalOpen={resetModalOpen}
      />

      {/* From lg the tabs and the top of the search box sit on the hero image,
          as on the original site; at xl the box used to start below it
          (WHIT-479). */}
      <SearchField
        withEmptyDiv
        withContainerPadding
        textInputVerticalInSmall
        containerClassName="absolute top-[24rem] sm:top-[22rem] md:top-[23rem] lg:top-[21rem]"
        inputContainerClassName="flex-col lg:flex-row flex-[2_2_0%] flex space-y-4 lg:space-y-0"
        isHome
        onTabChange={setTab}
      />

      {/* Travel simple... */}
      <Container
        id="section_1"
        containerClassName={`bg-tt-verylight-purple h-full flex justify-center ${SECTION_Y}`}
        className={`flex flex-col md:flex-row-reverse ${CARD_GAP}`}
      >
        <Image
          style={{ color: undefined }}
          src={travelSimple}
          // Mirrors the width classes below: the full width inside the page's
          // 20px side padding on phones, half the row from md up, and never
          // wider than 564px. md:shrink-0 holds that width before the image
          // loads: until then the flex row shrank it to about 315px on desktop,
          // and the load widened it and pushed the text (CLS 0.04).
          sizes="(max-width: 639px) calc(100vw - 40px), (max-width: 767px) 75vw, (max-width: 1279px) 50vw, 564px"
          className="self-center sm:w-3/4 md:w-2/4 md:shrink-0 3xl:w-[33.688rem] 3xl:h-[25rem] 2xl:w-[33.688rem] 2xl:h-[25rem]"
          alt=""
        />
        <div className="self-center">
          <H2 gopher className="w-full !text-[2.5rem] !leading-[2.625rem]">
            {t(
              'Travel simply, because with us you can arrange everything at the same time.',
            )}
          </H2>
          <P className="mt-4">
            {t(
              'More than 1,000 trusted travel partners across trains, buses, flights, ferries, and airport transfers, so that you can focus on the journey.',
            )}
          </P>
          <TransparentButton className="mt-4 w-[16.25rem]">
            {t('Get to know us')}
          </TransparentButton>
        </div>
      </Container>

      {/* WHIT-485: destination and theme pages, while the package pages
          are ours (WHIT-489). */}
      {PACKAGE_TOURS_IN_HOUSE && <HolidayIdeas />}
      {PACKAGE_TOURS_IN_HOUSE && (
        <RenderWhenNear>
          <TopOffers />
        </RenderWhenNear>
      )}

      {/* We give you some advice */}
      <RenderWhenNear>
        <TravelTips />
      </RenderWhenNear>

      {/* Your favorite */}
      <RenderWhenNear>
        <PopularDestinations />
      </RenderWhenNear>

      {/* There are times when */}
      {/* <Container containerClassName="bg-tt-verylight-purple">
        <H2 gopher>Van amikor a legolcsóbb kalandok a legmenőbbek</H2>
      </Container> */}

      {/* Notification input */}

      {/* Don't have an idea? */}
      {/* <RecommendedDestinatons /> */}

      {/* We will be your partner */}
      {/* <Container containerClassName="bg-tt-white-purple flex flex-col justify-center">
        <H2 gopher>Társad leszünk az utazásban</H2>
      </Container> */}
    </div>
  );
};

Home.getLayout = function getLayout(page: ReactElement) {
  return (
    <Layout
      titleProduction="meta_title_index"
      titleStaging="Welcome on Showcase"
      metaDescriptionProd="meta_description_production_index"
      metaDescriptionStaging="Allmyles API showcase search flights feature lets you find out our search features"
      canonicalProd="https://tipptour.com/"
      canonicalStaging="https://showcase.allmyles.com/"
    >
      {page}
    </Layout>
  );
};

export const getServerSideProps: GetServerSideProps = async ({
  locale,
  res,
}) => {
  // Next.js marks server-rendered pages no-store, which keeps the browser
  // from restoring this page instantly on Back (the back/forward cache).
  // The home page holds nothing per user in its HTML (sign-in state loads in
  // the browser), so it only needs revalidating, and no shared cache may keep it.
  res.setHeader('Cache-Control', 'private, no-cache');
  return {
    props: {
      ...(await serverSideTranslations(locale! || 'en')),
    } as IHomeProps,
  };
};

export default Home;
